
import React, { lazy } from 'react';

/**
 * Enhanced lazy import utility that ensures React is fully initialized before components are loaded
 */
export function lazyImport<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(() => {
    // Make sure React is fully initialized before importing
    // This helps prevent the "Cannot access 'R' before initialization" error
    if (typeof React !== 'undefined' && typeof React.createElement === 'function') {
      return factory();
    }
    
    // If React isn't initialized yet, delay the import
    // Use proper type casting to maintain the generic type throughout the Promise chain
    return new Promise<{ default: T }>((resolve) => {
      setTimeout(() => {
        factory().then((module) => resolve(module));
      }, 0);
    });
  });
}

/**
 * Preload components for faster navigation
 */
export const preloadComponents = (imports: Array<() => Promise<any>>) => {
  for (const importFn of imports) {
    try {
      // Use a setTimeout to ensure this doesn't block the main thread
      setTimeout(() => {
        importFn().catch(err => {
          // Silently catch errors since this is just preloading
          console.debug('Failed to preload component:', err);
        });
      }, 0);
    } catch (e) {
      // Ignore errors during preloading
      console.debug('Error starting preload:', e);
    }
  }
};
